import { useHelpWizardStore } from '@/features/products/store/helpWizard';
import { useSolutionStore } from '@/features/products/store/solution';
import { FilterGroup, type StepProps, type StepValue } from '@/features/products/types';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { SelectButton, type SelectButtonProps } from 'primereact/selectbutton';
import { Fragment } from 'react';

import Card from '../Card';
import styles from './Step.module.scss';

const buttonTemplate = (option: SelectButtonProps) => {
  const optionValue: StepValue = option.value;
  return <Card key={optionValue.value} title={optionValue.value} tooltip={option.tooltip ?? ''} />;
};


const renderButtons = (
  step: StepProps,
  handleSelectAll: (id: number, label: string, filterGroup: FilterGroup) => void,
  addValues: (value: StepValue[]) => void,
) => {
  const { groups, options, values, group: filterGroup } = step;
  if (groups?.length) {
    return [...groups]
      .sort((a, b) => (a.group < b.group ? -1 : 1))
      .map(({ group, options: opts, id }) => (
        <Fragment key={group}>
          <div className={styles.groupHeader}>
            <h4>{group}</h4>
            <Button
              onClick={() => {
                handleSelectAll(id, group, filterGroup);
              }}
              variant="outlined"
            >
              Select all
            </Button>
          </div>
          <SelectButton
            className={styles.selectButton}
            value={values}
            options={[...opts]?.sort((a, b) => (a.value.value < b.value.value ? -1 : 1))}
            onChange={(e) => {
              addValues(e.value);
            }}
            itemTemplate={buttonTemplate}
            multiple
          />
        </Fragment>
      ));
  }
  return (
    <SelectButton
      className={styles.selectButton}
      value={values}
      options={[...(options ?? [])].sort((a, b) => (a.value.value < b.value.value ? -1 : 1))}
      onChange={(e) => {
        addValues(e.value);
      }}
      itemTemplate={buttonTemplate}
      multiple
    />
  );
};

const Step = ({ index }: { index: number }) => {
  const steps = useHelpWizardStore((s) => s.steps);
  const filtersValue = useSolutionStore((s) => s.filtersValue);

  const step = steps[index];

  const findParent = (id: number, label: string, filterGroup: FilterGroup): StepValue[] => {
    const filter = filtersValue.find((b) => b.group === filterGroup);
    if (filter?.children?.length) {
      const selectedGroup = filter.children.find((b) => b.label === label);
      if (selectedGroup) {
        return [
          ...selectedGroup.values.map((v) => ({
            id: v.id,
            group: FilterGroup.BENEFIT,
            value: v.value,
            isParent: false,
          })),
          { id, group: filter.group, value: label, isParent: true },
        ].filter((v) => !step.values?.some((s) => s.value === v.value));
      }
    }
    return [];
  };

  const handleSelectAll = (id: number, label: string, filterGroup: FilterGroup) => {
    const values = findParent(id, label, filterGroup);
    const stepArr = steps.map((x, i) => (i === index ? { ...x, values: [...x.values, ...values] } : x));
    useHelpWizardStore.setState({ steps: stepArr });
  };

  const addValues = (values: StepValue[]) => {
    const stepArr = steps.map((x, i) => (i === index ? { ...x, values } : x));
    useHelpWizardStore.setState({ steps: stepArr });
  };

  return step ? (
    <section className={styles.container}>
      <h2>{step.title}</h2>
      <p>{step.content}</p>
      <div className={styles.cards}>{renderButtons(step, handleSelectAll, addValues)}</div>
    </section>
  ) : null;
};

export default Step;
